import * as React from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { usePortal } from "@/features/portal/portal-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TOKEN_DEFS } from "@/data/config";
import { generateThemeColorsFromUrl, type GeneratedPalette } from "@/data/brands-api";

/** The six colour tokens (in editor order, with their labels), derived from the
    canonical token list so it stays in sync as tokens are added or relabelled. */
const PREVIEW_TOKENS = TOKEN_DEFS.filter((d) => d.kind === "color").map((d) => ({
  key: d.id as keyof GeneratedPalette,
  label: d.label,
}));

/**
 * "Generate from a website": paste a URL, the backend reads its brand colours
 * and suggests a palette. The partner previews the six swatches and confirms
 * before they overwrite the open draft — nothing is saved until they Publish.
 */
export function ColorFromUrl() {
  const { activeBrand, onThemeChange } = usePortal();
  const [url, setUrl] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [pending, setPending] = React.useState<GeneratedPalette | null>(null);
  // The URL the pending palette was generated from (shown in the preview dialog).
  const [pendingUrl, setPendingUrl] = React.useState("");
  // A persistent failure message shown under the input — the page couldn't be
  // read, yielded no palette, or the service errored. Stays put until the next
  // attempt or a URL edit, so the partner doesn't miss a transient toast.
  const [error, setError] = React.useState<string | null>(null);

  const canGenerate = !!activeBrand && url.trim().length > 0 && !loading;

  async function handleGenerate() {
    const trimmed = url.trim();
    if (!trimmed || !activeBrand || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await generateThemeColorsFromUrl({ brandId: activeBrand.id, url: trimmed });
      if (result.status !== "SUCCESS" || !result.palette) {
        // The page couldn't be read or yielded no usable colours — surface the
        // backend's partner-facing explanation inline so it persists.
        setError(result.message ?? "Couldn’t read colors from that URL.");
        return;
      }
      setPending(result.palette);
      setPendingUrl(trimmed);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Couldn’t read colors from that URL.");
    } finally {
      setLoading(false);
    }
  }

  function applyPending() {
    if (!pending) return;
    // Reuse the same per-token setter the manual color pickers use, so the draft
    // goes dirty and the live preview updates exactly as a hand edit would.
    for (const { key } of PREVIEW_TOKENS) onThemeChange(key, pending[key]);
    setPending(null);
    toast.success("Colors applied — review and Publish to save.");
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-[12px] border border-border bg-panel-2/40 p-4">
      <Label className="flex items-center gap-1.5 text-[13px] font-semibold">
        <Sparkles className="size-4 text-accent" /> Generate from a website
      </Label>
      <p className="text-[13px] text-muted-foreground">
        Paste a URL and we’ll suggest a palette from its brand colors. Nothing saves until you Publish.
      </p>
      <div className="flex gap-2">
        <Input
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleGenerate();
            }
          }}
          placeholder="https://example.com"
          disabled={loading}
          className="text-[13px]"
        />
        <Button type="button" onClick={() => void handleGenerate()} disabled={!canGenerate} className="shrink-0">
          {loading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          {loading ? "Reading…" : "Generate"}
        </Button>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-[10px] border border-[rgba(255,90,90,.25)] bg-[rgba(255,90,90,.08)] px-3 py-2.5 text-[13px] leading-[1.45] text-[#ff8a8a]"
        >
          {error}
        </p>
      )}

      <Dialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suggested palette</DialogTitle>
            <DialogDescription>
              Generated from{" "}
              <span className="break-all font-medium text-foreground">{pendingUrl}</span>. Applying overwrites the
              current theme’s colors — it isn’t saved until you Publish.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {pending &&
              PREVIEW_TOKENS.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2.5">
                  <span
                    className="size-9 shrink-0 rounded-[10px] border border-border-strong"
                    style={{ background: pending[key] }}
                  />
                  <div className="leading-tight">
                    <div className="text-[13px] font-semibold">{label}</div>
                    <div className="font-mono text-[12px] uppercase tracking-[.04em] text-muted-foreground">
                      {pending[key]}
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="subtle" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={applyPending}>
              Use these colors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
